import * as preact from "preact";

import * as modalController from "modalController";
import { type PreactPageProps } from "pages/PreactPage";
import readlangClient, { requestErrorMessage } from "readlangClient";
import * as shareLink from "shareLink";
import * as languageMap from "shared/languageMap";
import { isValidUsername, userProfilePath } from "shared/usernameValidation";
import * as utils from "shared/utils";
import BookCollectionStore from "stores/BookCollectionStore";
import stores from "stores/stores";
import Button from "ui/Button";
import * as userBlocking from "userBlocking";
import { EditProfile } from "views/EditProfileView";
import { PublicLibraryList } from "views/PublicLibraryListView";

interface UserPageOptions {
  language?: string;
  userID: string;
}

interface UserProfile {
  _id: string;
  about?: string;
  completeHomepageURL?: string;
  emailMD5?: string;
  googlePlusInfo?: {
    image?: { url?: string };
    picture?: string;
  };
  homepageURL?: string;
  largeImageURL?: string;
  location?: string;
  username: string;
  displayName?: string;
}

interface SharedCount {
  language: string;
  shelfCount: number;
  textCount: number;
}

interface SharedCountView extends SharedCount {
  languageString: string;
  shelfPlural: boolean;
  textPlural: boolean;
}

interface SharedShelf {
  _id: string;
  books: unknown[];
  bookCount: number;
  description?: string;
  name: string;
}

interface State {
  books?: BookCollectionStore;
  error?: string;
  sharedCounts?: SharedCountView[];
  shelves?: SharedShelf[];
  user?: UserProfile;
}

export default class UserPageView extends preact.Component<
  PreactPageProps,
  State
> {
  state: State = {};
  private disposeUserWatch?: () => void;
  private mounted = false;
  private requestVersion = 0;

  componentDidMount() {
    this.mounted = true;
    this.disposeUserWatch = stores.user.onChange(
      (user) => user,
      () => {
        void this.loadPage();
      }
    );
    void this.loadPage();
  }

  componentDidUpdate(previousProps: PreactPageProps) {
    const previousOptions = previousProps.options as UserPageOptions;
    if (
      this.options.userID !== previousOptions.userID ||
      this.options.language !== previousOptions.language
    ) {
      void this.loadPage();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    this.disposeUserWatch?.();
  }

  private get options() {
    return this.props.options as UserPageOptions;
  }

  private async loadPage() {
    const requestVersion = ++this.requestVersion;
    const { language, userID } = this.options;

    localStorage.setItem("libraryOrDashboardPath", "/user/" + userID);
    this.setState({
      books: undefined,
      error: undefined,
      sharedCounts: undefined,
      shelves: undefined,
      user: undefined,
    });

    try {
      await this.loadContent(requestVersion, userID, language);
    } catch (error) {
      if (this.isCurrentRequest(requestVersion)) {
        this.setState({
          error:
            (error as any)?.jqXHR?.status === 404
              ? "No such user."
              : requestErrorMessage(error),
        });
      }
    }
  }

  private async loadContent(
    requestVersion: number,
    userID: string,
    language: string | undefined
  ) {
    const user = this.prepareUser(await this.fetchUser(userID));
    if (!this.isCurrentRequest(requestVersion)) {
      return;
    }

    this.setState({ user });

    // The URL parameter may be a username, but the shared-content endpoints key
    // off the canonical ObjectId. Use the id resolved by the profile fetch.
    const resolvedID = user._id;

    if (language) {
      const [shelves, books] = await Promise.all([
        this.fetchLanguageShelves(resolvedID, language),
        this.fetchLanguageBooks(resolvedID, language),
      ]);
      if (this.isCurrentRequest(requestVersion)) {
        this.setState({ books, shelves });
      }
    } else {
      const sharedCounts = await this.fetchSharedCounts(resolvedID);
      if (this.isCurrentRequest(requestVersion)) {
        this.setState({ sharedCounts });
      }
    }
  }

  private isCurrentRequest(requestVersion: number) {
    return this.mounted && this.requestVersion === requestVersion;
  }

  private async fetchUser(userID: string): Promise<UserProfile> {
    if (userID === stores.user.id.value) {
      return { ...stores.user.state.value } as UserProfile;
    }

    const { data } = await readlangClient.request<UserProfile>(
      "GET",
      "/api/user/" + userID,
      {}
    );
    return data;
  }

  private prepareUser(user: UserProfile): UserProfile {
    const preparedUser = { ...user };
    if (
      preparedUser.homepageURL &&
      preparedUser.homepageURL.length > 1 &&
      !/^http:\/\/|^https:\/\//.test(preparedUser.homepageURL)
    ) {
      preparedUser.completeHomepageURL = "http://" + preparedUser.homepageURL;
    } else {
      preparedUser.completeHomepageURL = preparedUser.homepageURL;
    }

    if (utils.childExists(preparedUser, "googlePlusInfo.image.url")) {
      preparedUser.largeImageURL =
        preparedUser.googlePlusInfo?.image?.url?.replace(/sz=50$/, "sz=120");
    }
    if (utils.childExists(preparedUser, "googlePlusInfo.picture")) {
      preparedUser.largeImageURL = preparedUser.googlePlusInfo?.picture;
    }

    return preparedUser;
  }

  private async fetchSharedCounts(userID: string): Promise<SharedCountView[]> {
    const { data } = await readlangClient.request<SharedCount[]>(
      "GET",
      "/api/user/" + userID + "/sharedBookCounts",
      {}
    );

    return data.map((languageInfo) => ({
      ...languageInfo,
      languageString:
        languageMap.map[languageInfo.language]?.english ??
        languageInfo.language,
      textPlural: languageInfo.textCount !== 1,
      shelfPlural: languageInfo.shelfCount !== 1,
    }));
  }

  private async fetchLanguageShelves(
    userID: string,
    language: string
  ): Promise<SharedShelf[]> {
    const { data } = await readlangClient.request<SharedShelf[]>(
      "GET",
      "/api/shelves",
      {
        language,
        userID,
      }
    );

    return data.map((shelf) => ({
      ...shelf,
      bookCount: shelf.books.length,
    }));
  }

  private async fetchLanguageBooks(userID: string, language: string) {
    const showCount = 999999;
    const { data } = await readlangClient.request<any[]>("GET", "/api/books", {
      ownerID: userID,
      language,
      sortBy: "totalVotes",
      limit: showCount + 1,
    });

    return new BookCollectionStore(data.slice(0, showCount));
  }

  private showEditProfile = () => {
    modalController.show(
      <EditProfile
        finished={function () {
          modalController.cancel();
        }}
      />,
      {
        title: "account",
      }
    );
  };

  private renderProfileRows(user: UserProfile) {
    return (
      <>
        {user.homepageURL && (
          <tr>
            <td>Website</td>
            <td class="profileField">
              <a href={user.completeHomepageURL}>{user.homepageURL}</a>
            </td>
          </tr>
        )}
        {user.location && (
          <tr>
            <td>Location</td>
            <td class="profileField">{user.location}</td>
          </tr>
        )}
        {user.about && (
          <tr>
            <td>About</td>
            <td class="profileField">{user.about}</td>
          </tr>
        )}
      </>
    );
  }

  private renderSharedSummary() {
    if (!this.state.sharedCounts?.length) {
      return null;
    }

    return (
      <>
        <h2>Shared content</h2>

        <ul>
          {this.state.sharedCounts.map((count) => (
            <li key={count.language}>
              <a
                href={`${userProfilePath(
                  this.state.user?.username,
                  this.state.user?._id || this.options.userID
                )}/texts/${count.language}`}
              >
                {count.textCount ? (
                  <>
                    {count.textCount} {count.languageString} text
                    {count.textPlural ? "s" : ""}
                    {count.shelfCount ? (
                      <>
                        {" "}
                        and {count.shelfCount}{" "}
                        {count.shelfPlural ? "shelves" : "shelf"}
                      </>
                    ) : null}
                  </>
                ) : (
                  <>
                    {count.shelfCount} {count.languageString} shelves
                  </>
                )}
              </a>
            </li>
          ))}
        </ul>
      </>
    );
  }

  private renderLanguageShelves(languageString: string) {
    if (!this.state.shelves || this.state.shelves.length === 0) {
      return null;
    }

    return (
      <>
        <h3>{languageString} shelves</h3>

        <ul>
          {this.state.shelves.map((shelf) => (
            <li class="shelfRow" style="width:100%" key={shelf._id}>
              <a href={`/shelf/${shelf._id}`}>
                <h4>
                  {shelf.name} ({shelf.bookCount} texts)
                </h4>
                <div class="noUnderline">{shelf.description}</div>
              </a>
            </li>
          ))}
        </ul>
      </>
    );
  }

  private renderLanguageSharedContent() {
    const language = this.options.language;
    if (!language) {
      return null;
    }

    const languageString = languageMap.map[language]?.english ?? language;

    return (
      <>
        <h2>
          <a
            href={userProfilePath(
              this.state.user?.username,
              this.state.user?._id || this.options.userID
            )}
          >
            Shared content
          </a>{" "}
          - {languageString}
        </h2>

        <div class="userPageSection">
          {this.renderLanguageShelves(languageString)}
        </div>
        <div class="userPageSection">
          <h3 style="margin-bottom:15px">{languageString} texts</h3>
          {this.state.books && <PublicLibraryList books={this.state.books} />}
        </div>
      </>
    );
  }

  render() {
    const { error, user } = this.state;
    if (error) {
      return <p>{error}</p>;
    }
    if (!user) {
      return null;
    }

    const editPossible = user._id === stores.user.id.value;
    const blocked = stores.user.isBlocked(user._id);

    return (
      <>
        <h2 class="spacing">{user.displayName || user.username}</h2>

        {/* Not a link: it's this page (Share profile shares it) */}
        {isValidUsername(user.username) && (
          <p class="profileUrl">/user/{user.username}</p>
        )}

        {user.largeImageURL ? (
          <img class="profilePic" src={user.largeImageURL} />
        ) : (
          <img
            class="profilePic"
            src={`//www.gravatar.com/avatar/${user.emailMD5}?d=mm&s=120`}
          />
        )}
        <div class="profileInfo">
          <table>{this.renderProfileRows(user)}</table>
        </div>
        <div class="clear"></div>
        <div class="profileActions">
          {editPossible && (
            <Button id="editProfile" onClick={this.showEditProfile}>
              Edit Your Profile
            </Button>
          )}
          {!editPossible &&
            !stores.user.isNew.value &&
            !stores.user.isLocalMode.value && (
              <Button
                id="blockUser"
                onClick={() => userBlocking.toggleBlocked(user)}
              >
                {blocked ? "Unblock" : "Block"}
              </Button>
            )}
          {isValidUsername(user.username) && (
            <Button
              icon="icon-share"
              onClick={() =>
                shareLink.shareLink({
                  title: user.displayName || user.username,
                  url: `${window.location.origin}/user/${user.username}`,
                })
              }
            >
              Share profile
            </Button>
          )}
        </div>

        <div class="sharedTexts">
          {blocked ? (
            <p>You've blocked {user.username}, so their texts are hidden.</p>
          ) : this.options.language ? (
            this.renderLanguageSharedContent()
          ) : (
            this.renderSharedSummary()
          )}
        </div>

        <div style="clear:both;height:15px"></div>
      </>
    );
  }
}
